// ==============================|| USER MERCHANT METADATA PREFERENCE STORE ||============================== //
//
// Module-scoped rather than a React context: five of the consuming call sites write from inside a
// `setState` updater, where a hook cannot be called. Callers pass the scope id explicitly and a
// cache miss goes through an injected resolver — this module never imports `useAuth` or Redux. See
// openspec/changes/tre-355-user-merchant-metadata-preferences/design.md.

import * as Sentry from '@sentry/react';

import { isPlainObject } from 'utils/parseUtils';
import { persistBag } from 'utils/user-merchant-metadata/persistUserMerchantMetadata';
import { migrateLegacyPreferences } from 'utils/user-merchant-metadata/userMerchantPreferencesMigration';

import type {
  UserMerchantPreferencesBag,
  UserMerchantPreferencesHydrationInput,
  UserMerchantPreferencesScope,
  UserMerchantPreferencesScopeResolver
} from 'model/types/user-merchant-preferences';

const WRITE_DEBOUNCE_MS = 500;

/** Ceiling on a caller that awaits the flush: `axiosGraphQLServices` sets no timeout of its own. */
export const PREFERENCES_FLUSH_TIMEOUT_MS = 2_000;

type PendingWrite = {
  scopeId: string;
  roleId: string;
  payload: UserMerchantPreferencesBag;
};

let cache: UserMerchantPreferencesScope | null = null;
let hydrationSignature: string | null = null;
let pendingWrite: PendingWrite | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let scopeResolver: UserMerchantPreferencesScopeResolver | null = null;

function matchesExpectedShape<T>(parsed: unknown, defaults: T): parsed is T {
  if (!isPlainObject(parsed)) {
    return false;
  }
  if (typeof defaults !== 'object' || defaults === null) {
    return true;
  }

  return Object.keys(defaults).every((key) => key in parsed);
}

function decodeBag(metadata: string): UserMerchantPreferencesBag {
  try {
    const parsed: unknown = JSON.parse(metadata);
    return isPlainObject(parsed) ? parsed : {};
  } catch {
    // an undecodable bag behaves as a bag the user has never written to, per spec
    return {};
  }
}

async function sendPendingWrite(): Promise<void> {
  const write = pendingWrite;
  if (!write) {
    return;
  }

  pendingWrite = null;
  await persistBag({ roleId: write.roleId, payload: write.payload });
}

function clearDebounce(): void {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
}

function queuePendingWrite({ scopeId, roleId, payload }: PendingWrite): void {
  const carried = pendingWrite?.scopeId === scopeId ? pendingWrite.payload : {};
  pendingWrite = { scopeId, roleId, payload: { ...carried, ...payload } };

  clearDebounce();
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    sendPendingWrite().catch(Sentry.captureException);
  }, WRITE_DEBOUNCE_MS);
}

export function hydrateUserMerchantPreferences({ scopeId, roleId, metadata }: UserMerchantPreferencesHydrationInput): void {
  const signature = JSON.stringify([scopeId, roleId, metadata]);
  if (signature === hydrationSignature) {
    return;
  }
  hydrationSignature = signature;

  flushUserMerchantPreferences().catch(Sentry.captureException);

  cache = migrateLegacyPreferences({ scopeId, roleId, bag: decodeBag(metadata) });
}

export function setUserMerchantPreferencesResolver(resolver: UserMerchantPreferencesScopeResolver | null): void {
  scopeResolver = resolver;
}

export function resetUserMerchantPreferences(): void {
  clearDebounce();
  pendingWrite = null;
  cache = null;
  hydrationSignature = null;
}

// Consumers read once, from a `useState`/`useMemo` initializer that runs before any ancestor effect
// can hydrate. Resolving the live scope on a cache miss makes a read correct whatever that ordering
// is; a resolved scope that does not match the caller's still leaves the scope guard to reject it.
function scopeFor(scopeId: string | null | undefined): UserMerchantPreferencesScope | null {
  if (!scopeId) {
    return null;
  }
  if (cache?.scopeId === scopeId) {
    return cache;
  }

  const resolved = scopeResolver?.();
  if (resolved?.scopeId === scopeId) {
    hydrateUserMerchantPreferences(resolved);
  }

  return cache?.scopeId === scopeId ? cache : null;
}

export function readUserMerchantPreferences<T>(namespace: string, scopeId: string | null | undefined, defaults: T): T {
  const scope = scopeFor(scopeId);
  if (!scope) {
    return defaults;
  }

  const stored: unknown = scope.bag[namespace];
  return matchesExpectedShape(stored, defaults) ? stored : defaults;
}

export function writeUserMerchantPreferences<T>(namespace: string, scopeId: string | null | undefined, value: T): void {
  // A write carrying a scope other than the hydrated one is dropped, never retargeted: its value was
  // derived from the other scope's read, so sending it here would corrupt this scope's bag.
  const scope = scopeFor(scopeId);
  if (!scope) {
    return;
  }

  cache = { ...scope, bag: { ...scope.bag, [namespace]: value } };
  queuePendingWrite({ scopeId: scope.scopeId, roleId: scope.roleId, payload: { [namespace]: value } });
}

async function withTimeout(task: Promise<void>, timeoutMs: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, timeoutMs);
  });

  try {
    await Promise.race([task, expiry]);
  } finally {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  }
}

export async function flushUserMerchantPreferences({ timeoutMs }: { timeoutMs?: number } = {}): Promise<void> {
  clearDebounce();
  const sent = sendPendingWrite();

  if (timeoutMs === undefined) {
    await sent;
    return;
  }

  await withTimeout(sent, timeoutMs);
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('pagehide', () => {
    flushUserMerchantPreferences().catch(Sentry.captureException);
  });
}
